import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';

// WhiteGreenDot component
const WhiteGreenDot = ({ size = "18px", dotColor = "#00ff00" }) => {
  return (
    <div 
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        backgroundColor: 'white',
        borderRadius: '50%'
      }}
    >
      <div 
        style={{
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '66%',
          height: '66%',
          backgroundColor: dotColor,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    </div>
  );
};

// ModelText component
const ModelText = ({ text, variant = "right", dotColor = "#00ff00", dotSize = "12px" }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: variant === 'right' ? 'row' : 'row-reverse',
      minWidth: '20vw',
      alignItems: 'center'
    }}>
      <div 
        style={{
          borderRadius: '50%',
          minWidth: dotSize,
          minHeight: dotSize,
          backgroundColor: dotColor,
          margin: 'auto 0',
          flexShrink: 0
        }}
      />
      <div 
        className="model-text"
        style={{
          position: 'relative',
          display: 'flex',
          fontSize: '18px',
          fontWeight: '600',
          color: '#425a70',
          overflowWrap: 'normal',
          zIndex: 1,
          paddingLeft: variant !== 'left' ? '10px' : '0',
          paddingRight: variant !== 'right' ? '10px' : '0',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </div>
    </div>
  );
};

export const Polyhedral = ({
  core,
  coreModification,
  coreColor,
  surfaceFunctionalization,
  surfaceFunctionalizationColor,
  lineColor = "#00ff00",
  baseRotation = [Math.PI / 10, 0, 0],
  isNanoformImageGenerated = false
}) => {
  const modelGroup = useRef();
  const y = baseRotation[1];

  useFrame(() => {
    if (modelGroup.current) {
      if (isNanoformImageGenerated) {
        modelGroup.current.rotation.y = y;
      } else {
        modelGroup.current.rotation.y += 0.005;
      }
    }
  });

  // Create cylinder geometries for polyhedral shape (pentagon-based cylinder)
  const coreCylinderGeometry = new THREE.CylinderGeometry(1.2, 1.2, 2.1, 5);
  const haloCylinderGeometry = new THREE.CylinderGeometry(1.35, 1.35, 2.25, 5);

  return (
    <group position={[0, 0, 0]}>
      {/* Core label */}
      {Boolean(core) && (
        <group>
          <Html className="core" position={[1, 0.95, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={coreModification ? `${core?.name}/${coreModification}` : (core?.name || 'Core')}
              variant="right"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[0, 0, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [0, 0, 2],
              [0.6, 0.95, 2],
              [1, 0.95, 2],
            ]}
            lineWidth={3}
            color={lineColor}
          />
        </group>
      )}

      {/* Surface functionalization label */}
      {Boolean(surfaceFunctionalization) && (
        <group>
          <Html className="core--left" position={[-0.9, 1, 2]} zIndexRange={[0, 1]}>
            <ModelText 
              dotSize="12px" 
              text={surfaceFunctionalization} 
              variant="left" 
              dotColor={lineColor} 
            />
          </Html>
          <Html className="center" position={[-0.05, 0.77, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [-0.05, 0.77, 2],
              [-0.4, 1, 2],
              [-0.9, 1, 2],
            ]}
            lineWidth={1.5}
            color={lineColor}
          />
        </group>
      )}

      {/* 3D Model */}
      <group ref={modelGroup} rotation={baseRotation}>
        {/* Core polyhedral (pentagonal cylinder) */}
        <mesh geometry={coreCylinderGeometry}>
          <meshStandardMaterial 
            attach="material" 
            roughness={0} 
            metalness={0.1} 
            color={coreColor} 
          />
        </mesh>

        {/* Halo polyhedral */}
        {Boolean(surfaceFunctionalization) && (
          <mesh geometry={haloCylinderGeometry}>
            <meshStandardMaterial 
              transparent 
              opacity={0.35} 
              color={surfaceFunctionalizationColor} 
            />
          </mesh>
        )}
      </group>
    </group>
  );
};